// Commissions → Plans & reps: each compensation plan with its versions, a
// "New version" form (old versions and past pay never change), the reps with
// their plan history, and the manual "Change plan / promote".
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  type AssignmentRow,
  type BonusTier,
  type CompRep,
  type PlanRow,
  type PlanVersionRow,
  errReason,
  fmtDay,
  money,
  ratePct,
  todayIso,
  usePlans,
} from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import { Empty, Field, INPUT, Loading, Notice, Pill, Section, TEXTAREA, T_HEAD, T_NUM, T_ROW, T_TD, T_TH, T_WRAP } from "./parts";

const PERIOD_WORD: Record<string, string> = { monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };

function tiersSummary(tiers: BonusTier[] | null): string {
  if (!tiers || tiers.length === 0) return "None";
  return (["monthly", "quarterly", "annual"] as const)
    .map((p) => {
      const ts = tiers.filter((t) => t.period === p).sort((a, b) => a.threshold - b.threshold);
      return ts.length ? `${PERIOD_WORD[p]}: ${ts.map((t) => `${money(t.threshold)} → ${money(t.amount)}`).join(", ")}` : null;
    })
    .filter(Boolean)
    .join(" · ");
}

export function PlansRepsTab() {
  const q = usePlans();
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string } | null>(null);
  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  if (!q.data) return <Loading />;
  const { plans, versions, assignments, reps, promotionFlags } = q.data;
  const flagged = promotionFlags.filter((f) => f.flagged);

  return (
    <div>
      {msg && (
        <Notice tone={msg.tone} className="mb-5">
          {msg.text}
        </Notice>
      )}

      {flagged.length > 0 && (
        <Notice tone="warn" className="mb-8">
          <span className="font-semibold">Review for promotion:</span>{" "}
          {flagged.map((f) => `${f.repName} (${money(f.annualized)} a year pace)`).join(", ")}. Promotion is manual — use “Change plan /
          promote” below.
        </Notice>
      )}

      {plans.map((p) => (
        <PlanBlock
          key={p.id}
          plan={p}
          versions={versions.filter((v) => v.plan_id === p.id).sort((a, b) => b.version - a.version)}
          onMsg={setMsg}
        />
      ))}

      <RepsTable reps={reps} plans={plans} assignments={assignments} onMsg={setMsg} />
    </div>
  );
}

function PlanBlock({
  plan,
  versions,
  onMsg,
}: {
  plan: PlanRow;
  versions: PlanVersionRow[];
  onMsg: (m: { tone: "ok" | "risk"; text: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = versions.find((v) => v.effective_from <= todayIso());
  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-3">
          {plan.name}
          {current ? (
            current.configured ? <Pill tone="active">Configured</Pill> : <Pill tone="warn">Not configured</Pill>
          ) : (
            <Pill tone="warn">Not configured</Pill>
          )}
        </span>
      }
      action={!open && <Button variant="outline" onClick={() => setOpen(true)}>Edit plan</Button>}
    >
      {open && (
        <NewVersionForm
          plan={plan}
          latest={versions[0]}
          onClose={() => setOpen(false)}
          onDone={(t) => {
            onMsg({ tone: "ok", text: t });
            setOpen(false);
          }}
        />
      )}
      {versions.length === 0 ? (
        <Empty>No versions yet.</Empty>
      ) : (
        <div className={T_WRAP}>
          <table className="w-full min-w-[1100px]">
            <thead className={T_HEAD}>
              <tr>
                <th className={T_TH}>Version</th>
                <th className={T_TH}>From</th>
                <th className={T_TH}>Status</th>
                <th className={`${T_TH} text-right`}>Commission rate</th>
                <th className={`${T_TH} text-right`}>Base salary</th>
                <th className={T_TH}>Revenue bonus tiers</th>
                <th className={`${T_TH} text-right`}>Development cap</th>
                <th className={T_TH}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className={T_ROW}>
                  <td className={`${T_TD} font-medium`}>
                    v{v.version}
                    {current?.id === v.id ? <span className="ml-2 text-[14px] font-normal text-muted">current</span> : null}
                  </td>
                  <td className={`${T_TD} whitespace-nowrap`}>{fmtDay(v.effective_from)}</td>
                  <td className={T_TD}>{v.configured ? "Configured" : <span className="text-warn">Not configured</span>}</td>
                  <td className={T_NUM}>{v.commission_rate != null ? `${ratePct(v.commission_rate)} of gross profit` : "—"}</td>
                  <td className={T_NUM}>{v.base_salary_annual != null ? `${money(v.base_salary_annual)} / yr` : "—"}</td>
                  <td className={`${T_TD} text-[14px]`}>{tiersSummary(v.revenue_bonus_tiers)}</td>
                  <td className={T_NUM}>{v.development_bonus_annual_cap != null ? `${money(v.development_bonus_annual_cap)} / yr` : "—"}</td>
                  <td className={`${T_TD} text-[14px] text-muted`}>{v.notes ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

interface TierDraft {
  period: BonusTier["period"];
  threshold: string;
  amount: string;
}

function numOrNull(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

function NewVersionForm({
  plan,
  latest,
  onClose,
  onDone,
}: {
  plan: PlanRow;
  latest: PlanVersionRow | undefined;
  onClose: () => void;
  onDone: (t: string) => void;
}) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(todayIso());
  const [configured, setConfigured] = useState(latest?.configured ?? true);
  const [ratePctStr, setRatePctStr] = useState(latest?.commission_rate != null ? String(Number((Number(latest.commission_rate) * 100).toFixed(4))) : "");
  const [base, setBase] = useState(latest?.base_salary_annual != null ? String(Number(latest.base_salary_annual)) : "");
  const [cap, setCap] = useState(latest?.development_bonus_annual_cap != null ? String(Number(latest.development_bonus_annual_cap)) : "");
  const [notes, setNotes] = useState("");
  const [tiers, setTiers] = useState<TierDraft[]>(
    (latest?.revenue_bonus_tiers ?? []).map((t) => ({ period: t.period, threshold: String(t.threshold), amount: String(t.amount) })),
  );
  const [err, setErr] = useState<string | null>(null);

  const rate = numOrNull(ratePctStr);
  const baseN = numOrNull(base);
  const capN = numOrNull(cap);
  const tierRows = tiers.map((t) => ({ period: t.period, threshold: Number(t.threshold), amount: Number(t.amount) }));
  const tiersOk = tierRows.every((t) => t.threshold > 0 && t.amount > 0);
  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(from) &&
    !Number.isNaN(rate) &&
    (rate == null || (rate >= 0 && rate <= 100)) &&
    (!configured || rate != null) &&
    !Number.isNaN(baseN) &&
    !Number.isNaN(capN) &&
    tiersOk;

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string; version: number }>(`/commissions/plans/${plan.id}/versions`, {
        method: "POST",
        body: JSON.stringify({
          effective_from: from,
          configured,
          commission_rate: rate == null ? null : Number((rate / 100).toFixed(6)),
          base_salary_annual: baseN,
          revenue_bonus_tiers: tierRows.length ? tierRows : null,
          development_bonus_annual_cap: capN,
          notes: notes.trim() || null,
        }),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["commissions"] });
      onDone(`${plan.name} version ${r.version} saved — it applies from ${fmtDay(from)}.`);
    },
    onError: (e) => setErr(errReason(e)),
  });

  return (
    <div className="mb-5 rounded-md border border-rule bg-white p-5">
      <p className="mb-4 text-[15px] text-muted">
        Starts from the plan as it is now — change or add what you need (e.g. + Add tier) and save. The changes apply from the
        date below; commissions already worked out keep the old numbers. (Saved as a new version, so the history stays.)
      </p>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <Field label="Applies from">
          <input type="date" className={INPUT} value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Commission rate (% of gross profit)" hint="e.g. 20 for Account Manager, 8 for Concierge">
          <input className={INPUT} inputMode="decimal" value={ratePctStr} onChange={(e) => setRatePctStr(e.target.value)} />
        </Field>
        <Field label="Base salary ($ a year)">
          <input className={INPUT} inputMode="decimal" value={base} onChange={(e) => setBase(e.target.value)} />
        </Field>
        <Field label="Development bonus cap ($ a year)">
          <input className={INPUT} inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)} />
        </Field>
        <label className="flex items-center gap-3 self-end pb-3 text-[15px] text-ink">
          <input type="checkbox" className="h-5 w-5" checked={configured} onChange={(e) => setConfigured(e.target.checked)} />
          Configured (ready to calculate pay)
        </label>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[14px] font-medium text-ink">Revenue bonus tiers</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTiers((ts) => [...ts, { period: "monthly", threshold: "", amount: "" }])}
          >
            <Plus className="h-4 w-4" aria-hidden /> Add tier
          </Button>
        </div>
        {tiers.length === 0 ? (
          <p className="text-[14px] text-muted">No revenue bonus tiers.</p>
        ) : (
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-3">
                <select
                  className={INPUT}
                  value={t.period}
                  onChange={(e) =>
                    setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, period: e.target.value as BonusTier["period"] } : x)))
                  }
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
                <input
                  className={INPUT}
                  inputMode="decimal"
                  placeholder="Revenue reached ($)"
                  aria-label="Revenue reached"
                  value={t.threshold}
                  onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, threshold: e.target.value } : x)))}
                />
                <input
                  className={INPUT}
                  inputMode="decimal"
                  placeholder="Bonus ($)"
                  aria-label="Bonus amount"
                  value={t.amount}
                  onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                />
                <Button variant="ghost" size="icon" aria-label="Remove tier" onClick={() => setTiers((ts) => ts.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5">
        <Field label="Notes">
          <textarea className={TEXTAREA} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
          Save changes
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {configured && rate == null && <span className="text-[14px] text-warn">A configured plan needs a commission rate.</span>}
        {err && <span className="text-[15px] text-risk">{err}</span>}
      </div>
    </div>
  );
}

function RepsTable({
  reps,
  plans,
  assignments,
  onMsg,
}: {
  reps: CompRep[];
  plans: PlanRow[];
  assignments: AssignmentRow[];
  onMsg: (m: { tone: "ok" | "risk"; text: string }) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const planName = (id: string) => plans.find((p) => p.id === id)?.name ?? "Plan";

  return (
    <Section
      title="Reps"
      intro="Changing a plan or promoting someone is always manual. It starts on the date you choose, keeps the full history, never removes a salary automatically, and never changes pay already calculated."
    >
      {reps.length === 0 ? (
        <Empty>No staff found.</Empty>
      ) : (
        <div className={T_WRAP}>
          <table className="w-full min-w-[1000px]">
            <thead className={T_HEAD}>
              <tr>
                <th className={T_TH}>Rep</th>
                <th className={T_TH}>Current plan</th>
                <th className={T_TH}>History</th>
                <th className={T_TH} />
              </tr>
            </thead>
            <tbody>
              {reps.map((r) => {
                const hist = assignments.filter((a) => a.user_id === r.id);
                return (
                  <tr key={r.id} className={T_ROW}>
                    <td className={T_TD}>
                      <span className="block font-medium">{r.full_name}</span>
                      {r.title ? <span className="text-[14px] text-muted">{r.title}</span> : null}
                    </td>
                    <td className={T_TD}>{r.current_plan ?? <span className="text-warn">No plan</span>}</td>
                    <td className={`${T_TD} text-[14px]`}>
                      {hist.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <ul className="space-y-1">
                          {hist.map((a) => (
                            <li key={a.id}>
                              <span className="font-medium">{planName(a.plan_id)}</span> from {fmtDay(a.effective_from)}
                              {a.effective_to ? ` to ${fmtDay(a.effective_to)}` : " — now"}
                              {a.reason ? <span className="text-muted"> · {a.reason}</span> : null}
                            </li>
                          ))}
                        </ul>
                      )}
                      {editing === r.id && (
                        <AssignForm
                          rep={r}
                          plans={plans}
                          onClose={() => setEditing(null)}
                          onDone={(t) => {
                            onMsg({ tone: "ok", text: t });
                            setEditing(null);
                          }}
                        />
                      )}
                    </td>
                    <td className={`${T_TD} whitespace-nowrap text-right`}>
                      {editing !== r.id && (
                        <Button variant="outline" onClick={() => setEditing(r.id)}>
                          Change plan / promote
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function AssignForm({
  rep,
  plans,
  onClose,
  onDone,
}: {
  rep: CompRep;
  plans: PlanRow[];
  onClose: () => void;
  onDone: (t: string) => void;
}) {
  const qc = useQueryClient();
  const [planId, setPlanId] = useState("");
  const [from, setFrom] = useState(todayIso());
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api("/commissions/assignments", {
        method: "POST",
        body: JSON.stringify({ user_id: rep.id, plan_id: planId, effective_from: from, reason: reason.trim() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["commissions"] });
      onDone(`${rep.full_name} moves to ${plans.find((p) => p.id === planId)?.name ?? "the new plan"} from ${fmtDay(from)}.`);
    },
    onError: (e) => setErr(errReason(e)),
  });
  return (
    <div className="mt-3 rounded-md border border-rule bg-canvas p-4">
      <div className="grid gap-3 md:grid-cols-[minmax(0,240px)_180px_minmax(0,1fr)]">
        <Field label="New plan">
          <select className={INPUT} value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">Choose…</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Starts on">
          <input type="date" className={INPUT} value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Why">
          <input className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Promoted to Account Manager" />
        </Field>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={!planId || !from || reason.trim().length < 2 || save.isPending}>
          Save plan change
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {err && <span className="text-[15px] text-risk">{err}</span>}
      </div>
    </div>
  );
}
